/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */
import { CATEGORY_NAME_INDEXES, SIZES } from '../../../../core/constants';
import { VIEW_TYPES } from '../../../../core/types';
import { RowHeights } from '../user-grid-settings/types';
import { getRowHeightSize } from '../user-grid-settings/user-grid-settings-actions';
import { ROW_KIND } from './constants';
import { hasPadding } from './format-rows';
import { ParentRowType, ChildRowType, RowType, TotalRow } from './types';

const ROW_SPACING = {
  small: 2,
  medium: 4,
  large: 8,
  xLarge: 30
};

const ROW_HEIGHTS = {
  small: 32,
  medium: 48,
  large: 96,
  xlarge: 160
};

export const getColors = (category: string, view?: string) => {
  const assetPalette = [
    'rgba(107, 91, 227, 0.5)',
    'rgba(220, 218, 246, 0.5)',
    'rgba(220, 218, 246, 0.5)',
    '#7D7DFF',
    'rgba(125, 125, 255, 0.5);'
  ];
  const cashAssetPalette = [
    'rgba(0, 246, 202, 0.49)',
    'rgba(0, 246, 202, 0.135757)',
    'rgba(220, 254, 248,0.5)',
    '#41CAA7',
    'rgba(104, 198, 171, 0.5)'
  ];
  const liabilityPalette = [
    'rgba(255,131,48,0.44)',
    'rgba(255,131,48,0.25)',
    'rgba(247, 232, 224,0.5)',
    '#E3A204',
    'rgba(255, 181, 0, 0.5)'
  ];
  const cashLiabilityPalette = [
    'rgba(255,0,59,0.45)',
    'rgba(247,217,227,0.5)',
    'rgba(247,217,227,0.5)',
    '#E63D53',
    'rgba(229, 61, 83, 0.5)'
  ];
  const isAssetCategory =
    category === CATEGORY_NAME_INDEXES.income ||
    category === CATEGORY_NAME_INDEXES.other_income ||
    category === CATEGORY_NAME_INDEXES.fixed_assets ||
    category === CATEGORY_NAME_INDEXES.current_assets ||
    category === CATEGORY_NAME_INDEXES.non_current_assets;
  const isLiabilityCategory =
    category === CATEGORY_NAME_INDEXES.expenses ||
    category === CATEGORY_NAME_INDEXES.cost_of_sales ||
    category === CATEGORY_NAME_INDEXES.other_expenses ||
    category === CATEGORY_NAME_INDEXES.current_liabilities ||
    category === CATEGORY_NAME_INDEXES.non_current_liabilities;

  if (isAssetCategory) {
    return view === VIEW_TYPES.CASH_FLOW ? cashAssetPalette : assetPalette;
  }
  if (isLiabilityCategory) {
    return view === VIEW_TYPES.CASH_FLOW ? cashLiabilityPalette : liabilityPalette;
  }
  return assetPalette;
};

export function nestedAccountHeightOverride(row: RowType, rowHeight: SIZES, level: number) {
  const isLargeSize = rowHeight === SIZES.LARGE;
  const isTotal = row.kind === ROW_KIND.total;
  const { isDisabled, forceRowSize } = row as ParentRowType;

  if (forceRowSize) {
    return forceRowSize;
  }

  // only totals that aren't disabled,
  if (isTotal && (row as TotalRow).disableLargeSize) {
    return isLargeSize ? SIZES.MEDIUM : rowHeight;
  }

  if (level > 0 || isDisabled) {
    return isLargeSize ? SIZES.MEDIUM : rowHeight;
  }

  return rowHeight;
}

export const getPaddingTop = (rowHeightOverride: SIZES, row: RowType, showNewPadding?: boolean) => {
  if (row.padding) {
    return ROW_SPACING.xLarge;
  }
  if (row.kind === ROW_KIND.heading) {
    return ROW_SPACING.large;
  }
  if (row.kind !== ROW_KIND.total && row.kind !== ROW_KIND.calculation) {
    if (row.level === 0) {
      return 0;
    }
    if (showNewPadding) {
      return ROW_SPACING.medium - (rowHeightOverride === SIZES.SMALL ? ROW_SPACING.medium : ROW_SPACING.small);
    }
    return ROW_SPACING.medium - (rowHeightOverride === SIZES.SMALL ? ROW_SPACING.small : 0);
  } else {
    if (row.level > 0) {
      return 0;
    }
    return ROW_SPACING.large - (rowHeightOverride === SIZES.SMALL ? ROW_SPACING.medium : 0);
  }
};

export const getWrapperStyle = (
  index: number,
  style: React.CSSProperties,
  rowHeight: SIZES,
  row: RowType,
  showNewPadding = false
): React.CSSProperties => {
  const isDisabled = (row as ParentRowType).isDisabled;
  const disabledStyles = isDisabled ? { opacity: 0.3 } : {};

  const styleOverride = {
    ...style,
    ...disabledStyles
  };

  const rowHeightOverride = nestedAccountHeightOverride(row, rowHeight, row.level) as SIZES;

  if (row.kind !== ROW_KIND.total && row.kind !== ROW_KIND.calculation && row.kind !== ROW_KIND.heading) {
    return {
      ...styleOverride,
      paddingTop: getPaddingTop(rowHeightOverride, row, showNewPadding),
      paddingBottom:
        ((row as unknown) as ChildRowType).siblingIndex === ((row as unknown) as ChildRowType).siblingCount - 1
          ? getPaddingTop(rowHeightOverride, row, showNewPadding)
          : 0
    };
  } else {
    if (!hasPadding(row, index)) {
      return styleOverride;
    }

    return {
      ...styleOverride,
      paddingTop: getPaddingTop(rowHeightOverride, row, showNewPadding)
    };
  }
};

export const getRowHeight = (index: number, rowHeights: RowHeights, row: RowType, showNewPadding = false) => {
  const rowHeight = getRowHeightSize(rowHeights);
  const rowHeightOverride = nestedAccountHeightOverride(row, rowHeight, row.level);
  const paddingTop = hasPadding(row, index) ? getPaddingTop(rowHeightOverride, row, showNewPadding) : 0;

  if (row.kind === ROW_KIND.heading) {
    return paddingTop + ROW_HEIGHTS.medium;
  }
  if (rowHeightOverride === SIZES.MEDIUM) {
    if (row.kind !== ROW_KIND.total && row.kind !== ROW_KIND.calculation) {
      return ((row as unknown) as ChildRowType).siblingIndex === 0 &&
        ((row as unknown) as ChildRowType).siblingCount > 1
        ? ROW_HEIGHTS.medium - getPaddingTop(rowHeightOverride, row, showNewPadding)
        : ROW_HEIGHTS.medium;
    } else {
      return paddingTop + ROW_HEIGHTS.medium;
    }
  }
  if (rowHeightOverride === SIZES.LARGE) {
    return paddingTop + ROW_HEIGHTS.large;
  }
  if (rowHeightOverride === SIZES.X_LARGE) {
    return paddingTop + ROW_HEIGHTS.xlarge;
  }

  return paddingTop + ROW_HEIGHTS.small;
};
